import axios, { AxiosResponse } from 'axios'
import { fetchJwt } from '..'

export const requestGet = async (url: string) => {
  try {
    // Add /api prefix for Strapi v4
    const apiUrl = url.startsWith('/api') ? url : `/api${url}`
    const jwt = fetchJwt()

    const res = await fetch(`${process.env.REACT_APP_API_HOST}${apiUrl}`, {
      method: 'get',
      headers: {
        authorization: `Bearer ${jwt}`
      }
    })

    if (res.ok) {
      return res.json()
    }

    // Log error details for debugging
    const errorBody = await res.text()
    console.error(`[requestGet] ${res.status} error for ${apiUrl}:`, errorBody)

    throw new Error('Failed fetching resource')
    // eslint-disable-next-line
  } catch (error) {
    console.log(error)
  }
}

export const requestPost = async (
  url: string,
  payload: any,
  onError?: (erroMessage?: string) => void
) => {
  try {
    if (url === '/auth/local') {
      // Use axios for auth endpoint - Strapi v4 requires /api prefix
      const authRes = await axios.post(`${process.env.REACT_APP_API_HOST}/api${url}`, payload)

      const { jwt, user } = authRes.data

      // Fetch complete user data with all relations populated
      const userRes = await axios.get(
        `${process.env.REACT_APP_API_HOST}/api/users/${user.id}?populate[language]=*&populate[company]=*&populate[role]=*&populate[division]=*&populate[region]=*&populate[profilePicture]=*&populate[programmes][populate][programme_types][populate][sub_modules][populate]=sections`,
        {
          headers: {
            Authorization: `Bearer ${jwt}`
          }
        }
      )

      // Flatten user data structure - extract from data/attributes pattern
      const flattenedUser = {
        id: userRes.data.id,
        ...userRes.data,
        // Extract language from nested structure
        language: userRes.data.language?.data
          ? {
              id: userRes.data.language.data.id,
              ...userRes.data.language.data.attributes
            }
          : userRes.data.language,
        // Extract company from nested structure
        company: userRes.data.company?.data
          ? {
              id: userRes.data.company.data.id,
              ...userRes.data.company.data.attributes
            }
          : userRes.data.company,
        // Extract role from nested structure
        role: userRes.data.role?.data
          ? {
              id: userRes.data.role.data.id,
              ...userRes.data.role.data.attributes
            }
          : userRes.data.role,
        // Extract division from nested structure
        division: userRes.data.division?.data
          ? {
              id: userRes.data.division.data.id,
              ...userRes.data.division.data.attributes
            }
          : userRes.data.division,
        // Extract region from nested structure
        region: userRes.data.region?.data
          ? {
              id: userRes.data.region.data.id,
              ...userRes.data.region.data.attributes
            }
          : userRes.data.region,
        // Extract profilePicture from nested structure
        profilePicture: userRes.data.profilePicture?.data
          ? {
              id: userRes.data.profilePicture.data.id,
              ...userRes.data.profilePicture.data.attributes
            }
          : userRes.data.profilePicture,
        // Extract programmes from nested structure
        programmes: userRes.data.programmes?.data
          ? userRes.data.programmes.data.map((prog: any) => ({
              id: prog.id,
              ...prog.attributes
            }))
          : userRes.data.programmes
      }

      // Return JWT with flattened user
      return {
        jwt,
        user: flattenedUser
      }
    } else {
      // Use fetch for other endpoints
      const res = await fetch(`${process.env.REACT_APP_API_HOST}${url}`, {
        method: 'post',
        headers: {
          'Content-Type': 'application/json',
          authorization: `Bearer ${fetchJwt()}`
        },
        body: JSON.stringify(payload)
      })

      if (!res.ok) {
        const errorData = await res.json()
        if (onError) {
          const errorMessage =
            errorData?.error?.message || errorData?.message || 'An error occurred'
          onError(errorMessage)
        }
        return
      }
      return res.json()
    }
  } catch (error) {
    if (onError) {
      onError()
    }
    console.error('Failed post request....', error)
  }
}
export const requestPut = async (url: string, payload: any, onSuccess?: (data: any) => void) => {
  try {
    // Add /api prefix for Strapi v4
    const apiUrl = url.startsWith('/api') ? url : `/api${url}`
    const token = await fetchJwt()
    const res = await fetch(`${process.env.REACT_APP_API_HOST}${apiUrl}`, {
      method: 'put',
      headers: {
        'Content-Type': 'application/json',
        authorization: `Bearer ${token}`
      },
      body: JSON.stringify(payload)
    })

    if (!res.ok) {
      const errText = await res.text()
      throw new Error(`Failed PUT request: ${res.status} ${errText}`)
    }
    const data = await res.json()
    if (onSuccess) {
      onSuccess(data)
    }
    return data
  } catch (error) {
    console.error('requestPut error:', error)
    throw error
  }
}

/**
 * The following are api calls for the server operations
 */
export const updateSectionSubmoduleSubmission = async ({
  subModuleSubmissionId,
  payload,
  onSuccess,
  onError
}: {
  subModuleSubmissionId: string
  payload: any
  onSuccess: () => void
  onError?: (errorMesage: string) => void
}) => {
  try {
    const { submoduleSubmissionData } = payload
    if (
      (submoduleSubmissionData.programme || submoduleSubmissionData.programme === 0) &&
      (submoduleSubmissionData.sub_module || submoduleSubmissionData.sub_module === 0)
    ) {
      await requestPut(`/submodule-submissions/${subModuleSubmissionId}`, {
        data: submoduleSubmissionData
      })
      onSuccess()
    } else if (onError) {
      if (!(submoduleSubmissionData.programme || submoduleSubmissionData.programme === 0)) {
        onError('Programme is missing from your account, please contact administrator')
      } else {
        onError('Submodule is missing from your account, please contact administrator')
      }
    }
  } catch (error) {
    if (onError) {
      onError('Failed updating submodule submission, check your internet')
    }
    console.log(error)
  }
}
